import { useState } from 'react';
import type { BasketItem } from '../page';

interface PrintReceiptModalProps {
  onClose: () => void;
  paymentMethods?: Array<{
    method: string;
    amount: number;
  }>;
  basketItems: BasketItem[];
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
}

export default function PrintReceiptModal({ 
  onClose, 
  paymentMethods = [],
  basketItems,
  subtotal,
  discount,
  tax,
  total
}: PrintReceiptModalProps) {
  const [printType, setPrintType] = useState<'customer' | 'merchant' | 'both'>('customer');
  const [copies, setCopies] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);

  const transactionData = {
    receiptNo: 'RCP-2024-001847',
    transactionId: 'TXN-20240115-084523',
    date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
    time: new Date().toLocaleTimeString('en-US'),
    cashier: 'Sarah Johnson',
    cashierId: 'CSH-042',
    terminal: 'Lane 3 - Terminal 01',
    bagFee: basketItems.length > 0 ? 0.25 : 0,
    paymentMethod: 'Cash',
    amountPaid: 0,
    change: 0,
    loyaltyPoints: Math.floor(total),
  };

  // Calculate total paid and change from payment methods
  const totalPaid = paymentMethods.reduce((sum, pm) => sum + pm.amount, 0);
  const change = totalPaid > total ? totalPaid - total : 0;

  const handlePrint = () => {
    setIsPrinting(true);
    
    setTimeout(() => {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const itemsHtml = basketItems.map(item => `
          <div class="item">
            <div class="item-name">${item.name}</div>
            <div class="item-details">
              <span>${item.qty} × $${item.price.toFixed(2)}</span>
              <span>$${item.total.toFixed(2)}</span>
            </div>
          </div>
        `).join('');

        // Generate payment methods HTML
        const paymentMethodsHtml = paymentMethods.length > 0 
          ? paymentMethods.map(pm => `
              <div class="total-row"><span>${pm.method}:</span><span>$${pm.amount.toFixed(2)}</span></div>
            `).join('')
          : `<div class="total-row"><span>Payment Method:</span><span>${transactionData.paymentMethod}</span></div>
             <div class="total-row"><span>Amount Paid:</span><span>$${transactionData.amountPaid.toFixed(2)}</span></div>`;

        const changeHtml = change > 0 
          ? `<div class="total-row" style="font-weight: bold;"><span>Change:</span><span>$${change.toFixed(2)}</span></div>`
          : '';

        const htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Receipt - ${transactionData.receiptNo}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                font-family: 'Courier New', monospace; 
                font-size: 12px; 
                line-height: 1.4;
                padding: 20px;
                max-width: 300px;
                margin: 0 auto;
              }
              .receipt { border: 1px solid #000; padding: 15px; }
              .header { text-align: center; margin-bottom: 15px; border-bottom: 2px dashed #000; padding-bottom: 10px; }
              .store-name { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
              .store-info { font-size: 10px; margin-bottom: 3px; }
              .section { margin: 15px 0; }
              .section-title { font-weight: bold; margin-bottom: 8px; border-bottom: 1px solid #000; padding-bottom: 3px; }
              .info-row { display: flex; justify-content: space-between; margin-bottom: 3px; font-size: 11px; }
              .items { margin: 15px 0; }
              .item { margin-bottom: 8px; }
              .item-name { font-weight: bold; margin-bottom: 2px; }
              .item-details { display: flex; justify-content: space-between; font-size: 11px; }
              .totals { border-top: 2px solid #000; padding-top: 10px; margin-top: 15px; }
              .total-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
              .grand-total { font-size: 16px; font-weight: bold; border-top: 2px solid #000; border-bottom: 2px solid #000; padding: 8px 0; margin: 10px 0; }
              .payment { margin: 15px 0; border-top: 1px dashed #000; padding-top: 10px; }
              .footer { text-align: center; margin-top: 15px; border-top: 2px dashed #000; padding-top: 10px; font-size: 10px; }
              .barcode { text-align: center; margin: 15px 0; font-size: 24px; letter-spacing: 2px; }
              @media print {
                body { padding: 0; }
                .receipt { border: none; }
              }
            </style>
          </head>
          <body>
            <div class="receipt">
              <div class="header">
                <div class="store-name">SUPERMART RETAIL</div>
                <div class="store-info">123 Commerce Street</div>
                <div class="store-info">New York, NY 10001</div>
                <div class="store-info">Tel: (555) 123-4567</div>
                <div class="store-info">Tax ID: 12-3456789</div>
              </div>

              <div class="section">
                <div class="info-row"><span>Receipt No:</span><span>${transactionData.receiptNo}</span></div>
                <div class="info-row"><span>Transaction:</span><span>${transactionData.transactionId}</span></div>
                <div class="info-row"><span>Date:</span><span>${transactionData.date}</span></div>
                <div class="info-row"><span>Time:</span><span>${transactionData.time}</span></div>
                <div class="info-row"><span>Cashier:</span><span>${transactionData.cashier}</span></div>
                <div class="info-row"><span>Terminal:</span><span>${transactionData.terminal}</span></div>
              </div>

              <div class="items">
                <div class="section-title">ITEMS PURCHASED</div>
                ${itemsHtml}
              </div>

              <div class="totals">
                <div class="total-row"><span>Subtotal:</span><span>$${subtotal.toFixed(2)}</span></div>
                <div class="total-row"><span>Discount:</span><span>-$${discount.toFixed(2)}</span></div>
                <div class="total-row"><span>Tax:</span><span>$${tax.toFixed(2)}</span></div>
                <div class="total-row"><span>Bag Fee:</span><span>$${transactionData.bagFee.toFixed(2)}</span></div>
                <div class="grand-total">
                  <div class="total-row"><span>TOTAL:</span><span>$${total.toFixed(2)}</span></div>
                </div>
              </div>

              <div class="payment">
                <div class="section-title">PAYMENT DETAILS</div>
                ${paymentMethodsHtml}
                ${changeHtml}
              </div>

              <div class="section">
                <div class="info-row"><span>Loyalty Points Earned:</span><span>+${transactionData.loyaltyPoints} pts</span></div>
              </div>

              <div class="barcode">||||| |||| |||||</div>
              <div style="text-align: center; font-size: 10px; margin-bottom: 15px;">${transactionData.receiptNo}</div>

              <div class="footer">
                <div style="margin-bottom: 5px;">Thank you for shopping with us!</div>
                <div style="margin-bottom: 5px;">Returns accepted within 30 days with receipt</div>
                <div style="margin-bottom: 5px;">Visit us at www.supermart.com</div>
                <div style="margin-top: 10px; font-weight: bold;">${printType === 'merchant' ? 'MERCHANT COPY' : printType === 'both' ? 'CUSTOMER COPY' : 'CUSTOMER COPY'}</div>
              </div>
            </div>
          </body>
          </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }

      setTimeout(() => {
        setIsPrinting(false);
        if (printType === 'both' && copies === 1) {
          onClose();
        } else {
          onClose();
        }
      }, 1000);
    }, 500);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gray-800 text-white px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold whitespace-nowrap">Print Receipt</h2>
            <p className="text-sm text-gray-300 whitespace-nowrap">Configure print settings and preview</p>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-700 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3 whitespace-nowrap">Receipt Type</label>
              <div className="space-y-2">
                <button
                  onClick={() => setPrintType('customer')}
                  className={`w-full p-3 rounded-lg border-2 transition-all cursor-pointer flex items-center gap-3 ${
                    printType === 'customer'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className={`w-5 h-5 flex items-center justify-center rounded-full border-2 transition-colors ${
                    printType === 'customer'
                      ? 'bg-teal-600 border-teal-600'
                      : 'border-gray-300'
                  }`}>
                    {printType === 'customer' && (
                      <i className="ri-check-line text-white text-xs"></i>
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">Customer Copy</p>
                    <p className="text-xs text-gray-600 whitespace-nowrap">Standard receipt for customer</p>
                  </div>
                </button>

                <button
                  onClick={() => setPrintType('merchant')}
                  className={`w-full p-3 rounded-lg border-2 transition-all cursor-pointer flex items-center gap-3 ${
                    printType === 'merchant'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className={`w-5 h-5 flex items-center justify-center rounded-full border-2 transition-colors ${
                    printType === 'merchant'
                      ? 'bg-teal-600 border-teal-600'
                      : 'border-gray-300'
                  }`}>
                    {printType === 'merchant' && (
                      <i className="ri-check-line text-white text-xs"></i>
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">Merchant Copy</p>
                    <p className="text-xs text-gray-600 whitespace-nowrap">Store record copy</p>
                  </div>
                </button>

                <button
                  onClick={() => setPrintType('both')}
                  className={`w-full p-3 rounded-lg border-2 transition-all cursor-pointer flex items-center gap-3 ${
                    printType === 'both'
                      ? 'border-teal-500 bg-teal-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className={`w-5 h-5 flex items-center justify-center rounded-full border-2 transition-colors ${
                    printType === 'both'
                      ? 'bg-teal-600 border-teal-600'
                      : 'border-gray-300'
                  }`}>
                    {printType === 'both' && (
                      <i className="ri-check-line text-white text-xs"></i>
                    )}
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">Both Copies</p>
                    <p className="text-xs text-gray-600 whitespace-nowrap">Print customer and merchant</p>
                  </div>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3 whitespace-nowrap">Number of Copies</label>
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setCopies(Math.max(1, copies - 1))}
                  className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-bold transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line"></i>
                </button>
                <input
                  type="number"
                  value={copies}
                  onChange={(e) => setCopies(Math.max(1, Math.min(5, parseInt(e.target.value) || 1)))}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg text-center text-lg font-bold focus:outline-none focus:border-teal-500"
                  min="1"
                  max="5"
                />
                <button
                  onClick={() => setCopies(Math.min(5, copies + 1))}
                  className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-bold transition-colors cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
              <p className="text-xs text-gray-600 whitespace-nowrap">Maximum 5 copies allowed</p>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <i className="ri-printer-line text-blue-600 text-lg mt-0.5"></i>
                  <div>
                    <p className="text-sm font-semibold text-blue-900 mb-1 whitespace-nowrap">Printer Status</p>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <p className="text-xs text-blue-800 whitespace-nowrap">Thermal Printer Ready</p>
                    </div>
                    <p className="text-xs text-blue-700 whitespace-nowrap">Paper: 78% remaining</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-bold text-gray-900 mb-3 whitespace-nowrap">Receipt Preview</h3>
            <div className="bg-white border border-gray-300 rounded p-4 max-w-xs mx-auto" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
              <div className="text-center border-b-2 border-dashed border-gray-400 pb-2 mb-2">
                <div className="font-bold text-base mb-1">SUPERMART RETAIL</div>
                <div className="text-xs">123 Commerce Street</div>
                <div className="text-xs">New York, NY 10001</div>
                <div className="text-xs">Tel: (555) 123-4567</div>
              </div>
              
              <div className="text-xs mb-2">
                <div className="flex justify-between mb-0.5">
                  <span>Receipt:</span>
                  <span>{transactionData.receiptNo}</span>
                </div>
                <div className="flex justify-between mb-0.5">
                  <span>Date:</span>
                  <span>{transactionData.date}</span>
                </div>
                <div className="flex justify-between">
                  <span>Cashier:</span>
                  <span>{transactionData.cashier}</span>
                </div>
              </div>

              <div className="border-t border-gray-400 pt-2 mb-2">
                <div className="text-xs font-bold mb-1">ITEMS ({basketItems.length})</div>
                {basketItems.map((item, idx) => (
                  <div key={idx} className="mb-1">
                    <div className="font-semibold">{item.name}</div>
                    <div className="flex justify-between text-xs">
                      <span>{item.qty} × ${item.price.toFixed(2)}</span>
                      <span>${item.total.toFixed(2)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t-2 border-gray-400 pt-2 text-xs">
                <div className="flex justify-between mb-0.5">
                  <span>Subtotal:</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-0.5">
                  <span>Discount:</span>
                  <span>-${discount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between mb-0.5">
                  <span>Tax:</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-sm border-t-2 border-gray-400 pt-1 mt-1">
                  <span>TOTAL:</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="border-t-2 border-dashed border-gray-400 pt-2 mt-2 text-xs">
                <div className="font-bold mb-1">PAYMENT DETAILS</div>
                {paymentMethods.length > 0 ? (
                  <>
                    {paymentMethods.map((pm, idx) => (
                      <div key={idx} className="flex justify-between mb-0.5">
                        <span>{pm.method}:</span>
                        <span>${pm.amount.toFixed(2)}</span>
                      </div>
                    ))}
                    {change > 0 && (
                      <div className="flex justify-between font-bold mt-1 pt-1 border-t border-gray-300">
                        <span>Change:</span>
                        <span>${change.toFixed(2)}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex justify-between mb-0.5">
                      <span>Method:</span>
                      <span>{transactionData.paymentMethod}</span>
                    </div>
                    <div className="flex justify-between mb-0.5">
                      <span>Paid:</span>
                      <span>${transactionData.amountPaid.toFixed(2)}</span>
                    </div>
                    {transactionData.change > 0 && (
                      <div className="flex justify-between font-bold">
                        <span>Change:</span>
                        <span>${transactionData.change.toFixed(2)}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="text-center text-xs mt-3 pt-2 border-t-2 border-dashed border-gray-400">
                <div className="mb-1">Thank you for shopping!</div>
                <div className="font-bold mt-2">{printType === 'merchant' ? 'MERCHANT COPY' : 'CUSTOMER COPY'}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
          >
            Cancel
          </button>
          
          <button
            onClick={handlePrint}
            disabled={isPrinting}
            className={`px-8 py-3 rounded-lg font-bold whitespace-nowrap transition-colors ${
              isPrinting
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer'
            }`}
          >
            {isPrinting ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Printing...
              </>
            ) : (
              <>
                <i className="ri-printer-line mr-2"></i>
                Print {copies > 1 ? `${copies} Copies` : 'Receipt'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
